import { useState } from 'react';
import { Archive, ClipboardList, Clock, DollarSign, Download, FileDown, Package, Receipt, TrendingUp, Truck, Users, X } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { formatCurrency, formatDate } from '../lib/format';

const reportTabs = [
  { key: 'sales', label: 'Penjualan', icon: DollarSign },
  { key: 'customers', label: 'Pelanggan', icon: Users },
  { key: 'suppliers', label: 'Supplier', icon: Truck },
  { key: 'po', label: 'Purchase Order', icon: ClipboardList },
  { key: 'inventory', label: 'Inventory', icon: Archive },
  { key: 'quotation', label: 'Quotation', icon: ClipboardList },
  { key: 'invoice', label: 'Invoice', icon: Receipt },
  { key: 'sj', label: 'Surat Jalan', icon: Truck },
];

const ReportModule = ({ customers, products, transactions, purchaseOrders, suppliers, settings }) => {
  const [activeTab, setActiveTab] = useState('sales');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  const monthFullNames = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

  const companyName = settings?.companyName || 'InvoicePro';

  // Get available years from all data
  const getAvailableYears = () => {
    const years = new Set();
    customers.forEach(c => { if (c.createdAt) years.add(new Date(c.createdAt).getFullYear()); });
    products.forEach(p => { if (p.createdAt) years.add(new Date(p.createdAt).getFullYear()); });
    transactions.forEach(t => { if (t.date) years.add(new Date(t.date).getFullYear()); });
    purchaseOrders.forEach(po => { if (po.date) years.add(new Date(po.date).getFullYear()); });
    const currentYear = new Date().getFullYear();
    years.add(currentYear);
    return Array.from(years).sort((a, b) => b - a);
  };

  const availableYears = getAvailableYears();

  // Filter data by date. Priority: explicit Dari/Sampai date range >
  // Year + Month combination > Year only.
  const filterByDate = (items, dateField) => {
    if (!startDate && !endDate && !selectedYear && !selectedMonth) return items;
    return items.filter(item => {
      const itemDate = item[dateField] ? new Date(item[dateField]) : null;
      if (!itemDate) return false;
      if (!startDate && !endDate && (selectedYear || selectedMonth)) {
        if (selectedYear && itemDate.getFullYear() !== parseInt(selectedYear)) return false;
        if (selectedMonth && (itemDate.getMonth() + 1) !== parseInt(selectedMonth)) return false;
        return true;
      }
      if (startDate && itemDate < new Date(startDate)) return false;
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (itemDate > end) return false;
      }
      return true;
    });
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    if (year) {
      setStartDate('');
      setEndDate('');
    } else {
      // Clearing year also clears month dependency to avoid confusing combos.
      setSelectedMonth('');
    }
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    if (month) {
      setStartDate('');
      setEndDate('');
      // Default to current year if user picks a month without choosing a year.
      if (!selectedYear) setSelectedYear(String(new Date().getFullYear()));
    }
  };

  const handleDateChange = (type, value) => {
    if (type === 'start') setStartDate(value);
    else setEndDate(value);
    if (value) {
      setSelectedYear('');
      setSelectedMonth('');
    }
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedYear('');
    setSelectedMonth('');
  };

  const hasActiveFilter = startDate || endDate || selectedYear || selectedMonth;

  // Group items by month based on a date field. Returns array of
  // { key: 'YYYY-MM', label: 'Bulan YYYY', count, items }.
  const groupByMonth = (items, dateField) => {
    const buckets = new Map();
    items.forEach(item => {
      const raw = item[dateField];
      if (!raw) return;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
      if (!buckets.has(key)) buckets.set(key, { key, label, count: 0, items: [] });
      const bucket = buckets.get(key);
      bucket.count += 1;
      bucket.items.push(item);
    });
    return Array.from(buckets.values()).sort((a, b) => a.key.localeCompare(b.key));
  };

  const getFilterLabel = () => {
    if (selectedYear || selectedMonth) {
      const monthLabel = selectedMonth ? monthFullNames[parseInt(selectedMonth) - 1] : '';
      const yearLabel = selectedYear || '';
      return [monthLabel, yearLabel].filter(Boolean).join(' ') || 'Semua Periode';
    }
    if (startDate && endDate) return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    if (startDate) return `Dari ${formatDate(startDate)}`;
    if (endDate) return `Sampai ${formatDate(endDate)}`;
    return 'Semua Periode';
  };

  const getReportData = () => {
    switch (activeTab) {
      case 'sales': {
        // Sales report: only paid invoices.
        const paidInvoices = transactions.filter(t => t.type === 'invoice' && (t.status || '').toLowerCase() === 'paid');
        const filtered = filterByDate(paidInvoices, 'date');
        // Build monthly revenue + count breakdown using transaction date.
        const monthly = (() => {
          const buckets = new Map();
          filtered.forEach(t => {
            if (!t.date) return;
            const d = new Date(t.date);
            if (isNaN(d.getTime())) return;
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            const label = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
            if (!buckets.has(key)) buckets.set(key, { key, label, count: 0, revenue: 0 });
            const b = buckets.get(key);
            b.count += 1;
            b.revenue += (t.total || 0);
          });
          return Array.from(buckets.values()).sort((a, b) => a.key.localeCompare(b.key));
        })();
        const totalRevenue = filtered.reduce((s, t) => s + (t.total || 0), 0);
        const avgRevenue = filtered.length ? totalRevenue / filtered.length : 0;
        return {
          title: 'Laporan Penjualan',
          headers: ['No', 'No. Invoice', 'Pelanggan', 'Tanggal', 'Total'],
          rows: filtered.map((t, i) => {
            const cust = customers.find(c => c.id === t.customerId);
            return [i + 1, t.id, cust?.name || t.customerName || '-', formatDate(t.date), formatCurrency(t.total)];
          }),
          filename: 'laporan-penjualan',
          monthlySales: monthly,
          summary: {
            totalInvoicesPaid: filtered.length,
            totalRevenue: formatCurrency(totalRevenue),
            avgRevenue: formatCurrency(avgRevenue),
            monthsActive: monthly.length,
          },
        };
      }
      case 'customers': {
        const filtered = filterByDate(customers, 'createdAt');
        const monthly = groupByMonth(filtered, 'createdAt');
        return {
          title: 'Laporan Pelanggan',
          headers: ['No', 'Nama', 'Email', 'Telepon', 'Kota', 'Tgl Dibuat'],
          rows: filtered.map((c, i) => [i + 1, c.name, c.email || '-', c.phone || '-', c.city || '-', formatDate(c.createdAt)]),
          filename: 'laporan-pelanggan',
          monthly,
          summary: {
            totalCustomers: filtered.length,
            monthsActive: monthly.length,
          },
        };
      }
      case 'suppliers': {
        const filtered = filterByDate(suppliers || [], 'createdAt');
        const monthly = groupByMonth(filtered, 'createdAt');
        return {
          title: 'Laporan Supplier',
          headers: ['No', 'Nama', 'Contact Person', 'Telepon', 'Email', 'Kota', 'Tgl Dibuat'],
          rows: filtered.map((s, i) => [i + 1, s.name, s.contactPerson || '-', s.phone || '-', s.email || '-', s.city || '-', formatDate(s.createdAt)]),
          filename: 'laporan-supplier',
          monthly,
          summary: {
            totalSuppliers: filtered.length,
            monthsActive: monthly.length,
          },
        };
      }
      case 'products': {
        const filtered = filterByDate(products, 'createdAt');
        return {
          title: 'Laporan Produk',
          headers: ['No', 'Nama Produk', 'Kategori', 'Stok', 'Satuan', 'Harga'],
          rows: filtered.map((p, i) => [i + 1, p.name, p.category || '-', p.stock, p.unit, formatCurrency(p.price)]),
          filename: 'laporan-produk',
        };
      }
      case 'po': {
        const statusLabels = { draft: 'Draft', ordered: 'Dipesan', received: 'Diterima', cancelled: 'Dibatalkan' };
        const filtered = filterByDate(purchaseOrders, 'date');
        return {
          title: 'Laporan Purchase Order',
          headers: ['No', 'No. PO', 'Nama Produk', 'Supplier', 'Tanggal', 'Status', 'Total'],
          rows: filtered.map((po, i) => {
            const productNames = po.items?.map(item => item.name).join(', ') || '-';
            return [i + 1, po.id, productNames, po.supplier, formatDate(po.date), statusLabels[po.status] || po.status, formatCurrency(po.total)];
          }),
          filename: 'laporan-purchase-order',
          summary: {
            totalPO: filtered.length,
            totalValue: formatCurrency(filtered.reduce((s, po) => s + (po.total || 0), 0)),
          },
        };
      }
      case 'inventory': {
        const filtered = filterByDate(products, 'createdAt');
        return {
          title: 'Laporan Inventory',
          headers: ['No', 'Nama Produk', 'Kategori', 'Stok', 'Satuan', 'Harga Satuan', 'Nilai Stok', 'Status'],
          rows: filtered.map((p, i) => {
            const status = p.stock <= 0 ? 'Habis' : p.stock < 10 ? 'Rendah' : p.stock <= 50 ? 'Normal' : 'Tinggi';
            return [i + 1, p.name, p.category || '-', p.stock, p.unit, formatCurrency(p.price), formatCurrency(p.stock * p.price), status];
          }),
          filename: 'laporan-inventory',
          summary: {
            totalProducts: filtered.length,
            lowStock: filtered.filter(p => p.stock > 0 && p.stock < 10).length,
            outOfStock: filtered.filter(p => p.stock <= 0).length,
            totalValue: formatCurrency(filtered.reduce((s, p) => s + p.stock * p.price, 0)),
          },
        };
      }
      case 'quotation':
        return getTxReport('quote', 'Quotation', 'laporan-quotation');
      case 'invoice':
        return getTxReport('invoice', 'Invoice', 'laporan-invoice');
      case 'sj':
        return getTxReport('sj', 'Surat Jalan', 'laporan-surat-jalan');
      default:
        return { title: '', headers: [], rows: [], filename: 'report' };
    }
  };

  const getTxReport = (type, label, filename) => {
    const filtered = filterByDate(transactions.filter(t => t.type === type), 'date');
    // Count transactions per status. For Quotation we expose the four
    // canonical statuses; Invoice uses {draft, pending, lunas, dibatalkan}
    // where "pending" maps to the "sent" status in DB and "lunas" to "paid".
    const statusBuckets = type === 'quote'
      ? { draft: 0, sent: 0, converted: 0, cancelled: 0 }
      : type === 'invoice'
      ? { draft: 0, pending: 0, lunas: 0, dibatalkan: 0 }
      : type === 'sj'
      ? { draft: 0, sent: 0, cancelled: 0 }
      : { draft: 0, sent: 0 };
    filtered.forEach(t => {
      const st = (t.status || 'draft').toLowerCase();
      if (type === 'invoice') {
        const mapped = st === 'sent' ? 'pending' : st === 'paid' ? 'lunas' : st === 'cancelled' ? 'dibatalkan' : st === 'draft' ? 'draft' : null;
        if (mapped && mapped in statusBuckets) statusBuckets[mapped] += 1;
      } else {
        if (st in statusBuckets) statusBuckets[st] += 1;
      }
    });
    const summary = {
      totalTransactions: filtered.length,
      totalValue: formatCurrency(filtered.reduce((s, t) => s + (t.total || 0), 0)),
    };
    // Inject status counts so they render in the summary cards & exports.
    Object.entries(statusBuckets).forEach(([key, val]) => {
      summary[`status_${key}`] = val;
    });
    return {
      title: `Laporan ${label}`,
      headers: ['No', `No. ${label}`, 'Pelanggan', 'Tanggal', 'Status', 'Total'],
      rows: filtered.map((t, i) => {
        const cust = customers.find(c => c.id === t.customerId);
        return [i + 1, t.id, cust?.name || '-', formatDate(t.date), t.status?.toUpperCase() || '-', formatCurrency(t.total)];
      }),
      filename,
      summary,
    };
  };

  const exportPDF = () => {
    const report = getReportData();
    const doc = new jsPDF('l', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.text(companyName, 14, 18);
    doc.setFontSize(12);
    doc.text(report.title, 14, 26);
    doc.setFontSize(9);
    doc.text(`Tanggal: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, 14, 32);
    if (hasActiveFilter) {
      doc.text(`Periode: ${getFilterLabel()}`, 14, 37);
    }

    autoTable(doc, {
      head: [report.headers],
      body: report.rows,
      startY: hasActiveFilter ? 43 : 38,
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
    });

    if (report.monthlySales && report.monthlySales.length > 0) {
      const startY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(10);
      doc.text('Pendapatan per Bulan', 14, startY);
      autoTable(doc, {
        head: [['Bulan', 'Jumlah Invoice Lunas', 'Pendapatan']],
        body: report.monthlySales.map(m => [m.label, m.count, formatCurrency(m.revenue)]),
        startY: startY + 3,
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 14, right: 14 },
      });
    }

    if (report.monthly && report.monthly.length > 0) {
      const startY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(10);
      doc.text(activeTab === 'customers' ? 'Pelanggan Baru per Bulan' : 'Supplier Baru per Bulan', 14, startY);
      autoTable(doc, {
        head: [['Bulan', activeTab === 'customers' ? 'Jumlah Pelanggan Baru' : 'Jumlah Supplier Baru']],
        body: report.monthly.map(m => [m.label, m.count]),
        startY: startY + 3,
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [51, 65, 85], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 14, right: 14 },
      });
    }

    if (report.summary) {
      const finalY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(9);
      const entries = Object.entries(report.summary);
      const summaryLabels = {
        totalProducts: 'Total Produk', lowStock: 'Stok Rendah', outOfStock: 'Stok Habis',
        totalValue: 'Total Nilai', totalTransactions: 'Total Transaksi', totalPO: 'Total PO',
        totalCustomers: 'Total Pelanggan Baru', totalSuppliers: 'Total Supplier Baru', monthsActive: 'Bulan Aktif',
        status_draft: 'Draft', status_sent: 'Terkirim', status_converted: 'Dikonversi',
        status_cancelled: 'Dibatalkan', status_paid: 'Dibayar',
        status_pending: 'Pending', status_lunas: 'Lunas', status_dibatalkan: 'Dibatalkan',
        totalInvoicesPaid: 'Invoice Lunas', totalRevenue: 'Total Pendapatan', avgRevenue: 'Rata-rata per Invoice',
      };
      entries.forEach(([k, v], idx) => {
        doc.text(`${summaryLabels[k] || k}: ${v}`, 14, finalY + idx * 6);
      });
    }

    doc.setFontSize(7);
    doc.text(`Dicetak oleh ${companyName} - ${new Date().toLocaleString('id-ID')}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 8, { align: 'center' });

    doc.save(`${report.filename}.pdf`);
  };

  const exportExcel = () => {
    const report = getReportData();
    const wsData = [report.headers, ...report.rows];
    if (report.monthlySales && report.monthlySales.length > 0) {
      wsData.push([]);
      wsData.push(['Pendapatan per Bulan']);
      wsData.push(['Bulan', 'Jumlah Invoice Lunas', 'Pendapatan']);
      report.monthlySales.forEach(m => wsData.push([m.label, m.count, m.revenue]));
    }
    if (report.monthly && report.monthly.length > 0) {
      wsData.push([]);
      wsData.push([activeTab === 'customers' ? 'Pelanggan Baru per Bulan' : 'Supplier Baru per Bulan']);
      wsData.push(['Bulan', 'Jumlah']);
      report.monthly.forEach(m => wsData.push([m.label, m.count]));
    }
    if (report.summary) {
      wsData.push([]);
      Object.entries(report.summary).forEach(([k, v]) => {
        const labels = { totalProducts: 'Total Produk', lowStock: 'Stok Rendah', outOfStock: 'Stok Habis', totalValue: 'Total Nilai', totalTransactions: 'Total Transaksi', totalPO: 'Total PO', totalCustomers: 'Total Pelanggan Baru', totalSuppliers: 'Total Supplier Baru', monthsActive: 'Bulan Aktif', status_draft: 'Draft', status_sent: 'Terkirim', status_converted: 'Dikonversi', status_cancelled: 'Dibatalkan', status_paid: 'Dibayar', status_pending: 'Pending', status_lunas: 'Lunas', status_dibatalkan: 'Dibatalkan', totalInvoicesPaid: 'Invoice Lunas', totalRevenue: 'Total Pendapatan', avgRevenue: 'Rata-rata per Invoice' };
        wsData.push([labels[k] || k, v]);
      });
    }
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const colWidths = report.headers.map((h, i) => {
      const maxLen = Math.max(h.length, ...report.rows.map(r => String(r[i] || '').length));
      return { wch: Math.min(maxLen + 4, 40) };
    });
    ws['!cols'] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, report.title.substring(0, 31));
    XLSX.writeFile(wb, `${report.filename}.xlsx`);
  };

  const report = getReportData();

  return (
    <div className="space-y-6 animate-in">
      <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Laporan</h2>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Export data dalam format PDF dan Excel</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2">
          <button
            onClick={exportPDF}
            disabled={report.rows.length === 0}
            className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="export-pdf-btn"
          >
            <FileDown size={16} />
            <span className="whitespace-nowrap">Export PDF</span>
          </button>
          <button
            onClick={exportExcel}
            disabled={report.rows.length === 0}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-50"
            data-testid="export-excel-btn"
          >
            <Download size={16} />
            <span className="whitespace-nowrap">Export Excel</span>
          </button>
        </div>
      </div>

      {/* Report Tabs */}
      <div className="card p-1.5">
        <div className="flex gap-1 overflow-x-auto no-scrollbar -mx-0.5 px-0.5">
          {reportTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.key
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-slate-100'
                }`}
                data-testid={`report-tab-${tab.key}`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Date Filter */}
      <div className="card p-4">
        <div className="grid grid-cols-2 lg:flex lg:flex-wrap lg:items-end gap-3 lg:gap-4">
          <div className="flex flex-col space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Dari Tanggal</label>
            <input
              type="date"
              className="input-field text-sm h-10 w-full lg:w-44"
              value={startDate}
              onChange={e => handleDateChange('start', e.target.value)}
              data-testid="report-filter-start-date"
            />
          </div>
          <div className="flex flex-col space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Sampai Tanggal</label>
            <input
              type="date"
              className="input-field text-sm h-10 w-full lg:w-44"
              value={endDate}
              onChange={e => handleDateChange('end', e.target.value)}
              data-testid="report-filter-end-date"
            />
          </div>
          <div className="flex flex-col space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tahun</label>
            <select
              className="input-field bg-white text-sm h-10 w-full lg:w-32"
              value={selectedYear}
              onChange={e => handleYearChange(e.target.value)}
              data-testid="report-filter-year"
            >
              <option value="">Semua</option>
              {availableYears.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Bulan</label>
            <select
              className="input-field bg-white text-sm h-10 w-full lg:w-40"
              value={selectedMonth}
              onChange={e => handleMonthChange(e.target.value)}
              data-testid="report-filter-month"
            >
              <option value="">Semua Bulan</option>
              {monthFullNames.map((name, idx) => (
                <option key={idx} value={String(idx + 1)}>{name}</option>
              ))}
            </select>
          </div>
          {hasActiveFilter && (
            <button
              onClick={clearFilters}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 h-10 text-sm text-rose-600 hover:bg-rose-50 rounded-lg transition-colors col-span-2 lg:col-span-1"
              data-testid="report-filter-clear"
            >
              <X size={14} />
              Hapus Filter
            </button>
          )}
          {hasActiveFilter && (
            <div className="flex items-center justify-center gap-2 px-3 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium col-span-2 lg:col-span-1">
              <Clock size={14} />
              {getFilterLabel()}
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {report.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(report.summary).map(([key, val]) => {
            const labels = { totalProducts: 'Total Produk', lowStock: 'Stok Rendah', outOfStock: 'Stok Habis', totalValue: 'Total Nilai', totalTransactions: 'Total Transaksi', totalPO: 'Total PO', totalCustomers: 'Total Pelanggan Baru', totalSuppliers: 'Total Supplier Baru', monthsActive: 'Bulan Aktif', status_draft: 'Draft', status_sent: 'Terkirim', status_converted: 'Dikonversi', status_cancelled: 'Dibatalkan', status_paid: 'Dibayar', status_pending: 'Pending', status_lunas: 'Lunas', status_dibatalkan: 'Dibatalkan', totalInvoicesPaid: 'Invoice Lunas', totalRevenue: 'Total Pendapatan', avgRevenue: 'Rata-rata per Invoice' };
            const statusStyles = {
              status_draft: { dot: 'bg-slate-400', text: 'text-slate-700' },
              status_sent: { dot: 'bg-sky-500', text: 'text-sky-700' },
              status_pending: { dot: 'bg-amber-500', text: 'text-amber-700' },
              status_converted: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
              status_paid: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
              status_lunas: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
              status_cancelled: { dot: 'bg-rose-500', text: 'text-rose-700' },
              status_dibatalkan: { dot: 'bg-rose-500', text: 'text-rose-700' },
            };
            const isStatus = key.startsWith('status_');
            const style = statusStyles[key];
            return (
              <div key={key} className="card p-4" data-testid={`summary-${key}`}>
                <div className="flex items-center gap-2">
                  {isStatus && style && <span className={`w-2 h-2 rounded-full ${style.dot}`} />}
                  <p className={`text-sm ${isStatus && style ? style.text : 'text-muted-foreground'}`}>
                    {labels[key] || key}
                  </p>
                </div>
                <p className="text-xl font-bold mt-1">{val}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Monthly Sales (Penjualan) */}
      {report.monthlySales && activeTab === 'sales' && (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-border/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} className="text-primary" />
              <h3 className="font-semibold" data-testid="monthly-sales-title">Pendapatan per Bulan</h3>
            </div>
            <span className="text-sm text-muted-foreground">
              {report.monthlySales.length} bulan
            </span>
          </div>

          {report.monthlySales.length === 0 ? (
            <div className="px-6 py-12 text-center text-muted-foreground text-sm">
              Tidak ada invoice lunas pada periode yang dipilih.
            </div>
          ) : (
            <div className="p-6 space-y-6">
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={report.monthlySales} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}jt` : v >= 1_000 ? `${(v / 1_000).toFixed(0)}rb` : v}
                    />
                    <Tooltip
                      contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0', fontSize: 12 }}
                      formatter={(value, name) => name === 'revenue' ? [formatCurrency(value), 'Pendapatan'] : [value, 'Jumlah Invoice']}
                    />
                    <Bar dataKey="revenue" radius={[8, 8, 0, 0]}>
                      {report.monthlySales.map((entry, idx) => (
                        <Cell key={idx} fill="#0891A1" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="monthly-sales-table">
                  <thead className="table-header">
                    <tr>
                      <th className="px-4 py-3 text-left">Bulan</th>
                      <th className="px-4 py-3 text-right">Jumlah Invoice Lunas</th>
                      <th className="px-4 py-3 text-right">Pendapatan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.monthlySales.map((m) => (
                      <tr key={m.key} className="table-row" data-testid={`monthly-sales-row-${m.key}`}>
                        <td className="px-4 py-3">{m.label}</td>
                        <td className="px-4 py-3 text-right">{m.count}</td>
                        <td className="px-4 py-3 text-right font-semibold">{formatCurrency(m.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Monthly Breakdown (Pelanggan / Supplier) */}
      {report.monthly && (activeTab === 'customers' || activeTab === 'suppliers') && (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-border/60 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} className="text-primary" />
              <h3 className="font-semibold" data-testid="monthly-breakdown-title">
                {activeTab === 'customers' ? 'Pelanggan Baru per Bulan' : 'Supplier Baru per Bulan'}
              </h3>
            </div>
            <span className="text-sm text-muted-foreground">
              {report.monthly.length} bulan
            </span>
          </div>

          {report.monthly.length === 0 ? (
            <div className="px-6 py-12 text-center text-muted-foreground text-sm">
              Tidak ada data pada periode yang dipilih.
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Bar chart */}
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={report.monthly} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '12px', border: '1px solid #E2E8F0', fontSize: 12 }}
                      formatter={(value) => [`${value} ${activeTab === 'customers' ? 'pelanggan' : 'supplier'}`, 'Jumlah']}
                    />
                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                      {report.monthly.map((entry, idx) => (
                        <Cell key={idx} fill="#0891A1" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Monthly table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="monthly-breakdown-table">
                  <thead className="table-header">
                    <tr>
                      <th className="px-4 py-3 text-left">Bulan</th>
                      <th className="px-4 py-3 text-right">
                        Jumlah {activeTab === 'customers' ? 'Pelanggan Baru' : 'Supplier Baru'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.monthly.map((m) => (
                      <tr key={m.key} className="table-row" data-testid={`monthly-row-${m.key}`}>
                        <td className="px-4 py-3">{m.label}</td>
                        <td className="px-4 py-3 text-right font-semibold">{m.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Report Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-border/60 flex items-center justify-between">
          <h3 className="font-semibold" data-testid="report-title">{report.title}</h3>
          <span className="text-sm text-muted-foreground">{report.rows.length} data</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="table-header">
              <tr>
                {report.headers.map((h, i) => (
                  <th key={i} className={`px-6 py-4 ${i === 0 ? 'text-left' : i >= report.headers.length - 1 ? 'text-right' : 'text-left'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.rows.length === 0 ? (
                <tr>
                  <td colSpan={report.headers.length} className="px-6 py-12 text-center text-muted-foreground">
                    Tidak ada data untuk ditampilkan.
                  </td>
                </tr>
              ) : report.rows.map((row, ri) => (
                <tr key={ri} className="table-row">
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-6 py-3 ${ci === 0 ? 'text-muted-foreground' : ''} ${ci >= row.length - 1 ? 'text-right font-mono' : 'text-left'}`}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============== SALES MODULE ==============


export default ReportModule;
